'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { useChildDetails } from '@sola/my-sdk/children/hooks.ts';
import type { BlockProps } from '@sola/schema/lib/schema.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { commitmentsQuery } from '@sola/supporter-sdk/queries.ts';
import { BackButton } from '../../atoms/back-button/back-button.tsx';
import { ChildBioAccordion } from '../../molecules/child-bio-accordion/child-bio-accordion.tsx';
import { ChildBioSummary } from '../child-bio-summary/child-bio-summary.tsx';
const useComponentData = id => {
  const $ = _c(13);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== id || $[1] !== searchParams) {
    t0 = id ?? searchParams.get("gid");
    $[0] = id;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const globalId = t0;
  const {
    data: child,
    isLoading: isChildLoading,
    isFetching: isChildFetching,
    isPending: isChildPending,
    isError: isChildError
  } = useChildDetails(globalId ?? undefined);
  const {
    data: commitments,
    isLoading: isCommitmentsLoading,
    isFetching: isCommitmentsFetching,
    isPending: isCommitmentsPending,
    isError: isCommitmentsError
  } = useSupporterQuery(commitmentsQuery);
  let t1;
  if ($[3] !== commitments || $[4] !== globalId) {
    t1 = commitments?.find(c => c.participant?.globalId === globalId);
    $[3] = commitments;
    $[4] = globalId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const commitment = t1;
  const isLoading = isChildLoading || isCommitmentsLoading;
  const isFetching = isChildFetching || isCommitmentsFetching;
  const isPending = isChildPending || isCommitmentsPending;
  const isError = isChildError || isCommitmentsError;
  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const isShowingChild = !isShowingSkeletons && !!child && !!commitment;
  const isShowingError = !isShowingSkeletons && (isError || !child && !!globalId);
  const commitmentCount = commitments?.length ?? 0;
  let t2;
  if ($[6] !== child || $[7] !== commitment || $[8] !== commitmentCount || $[9] !== isShowingChild || $[10] !== isShowingError || $[11] !== isShowingSkeletons) {
    t2 = {
      child,
      commitment,
      commitmentCount,
      isShowingSkeletons,
      isShowingChild,
      isShowingError
    };
    $[6] = child;
    $[7] = commitment;
    $[8] = commitmentCount;
    $[9] = isShowingChild;
    $[10] = isShowingError;
    $[11] = isShowingSkeletons;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
const ChildBioSkeleton: React.FC = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-snug-md"><Skeleton height={40} width={100} /><Skeleton height={24} width={225} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-comfy-xl">{t0}<Skeleton width="100%" height="auto" style={{
        aspectRatio: "16/9"
      }} borderRadius="1em" /><Skeleton width="100%" height={90} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
type ChildBioProps = BlockProps & {
  id?: string;
};
const ChildBioContent: React.FC<ChildBioProps> = t0 => {
  const $ = _c(18);
  const {
    id
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    child,
    commitment,
    commitmentCount,
    isShowingSkeletons,
    isShowingChild,
    isShowingError
  } = useComponentData(id);
  let t2;
  if ($[1] !== commitmentCount || $[2] !== t) {
    t2 = commitmentCount > 0 && <BackButton link={t("sponsorshipRelationship.childBio.backButton.url")} label={t("sponsorshipRelationship.childBio.backButton.text")} />;
    $[1] = commitmentCount;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isShowingSkeletons) {
    t3 = isShowingSkeletons && <ChildBioSkeleton />;
    $[4] = isShowingSkeletons;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== child || $[7] !== commitment || $[8] !== isShowingChild) {
    t4 = isShowingChild && child && commitment && <div className="flex flex-col gap-comfy-xl"><ChildBioSummary details={child} commitment={commitment} /><ChildBioAccordion details={child} commitment={commitment} /></div>;
    $[6] = child;
    $[7] = commitment;
    $[8] = isShowingChild;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isShowingError || $[11] !== t) {
    t5 = isShowingError && <Alert variant="negative" title={t("sponsorshipRelationship.childBio.alert.title")} message={t("sponsorshipRelationship.childBio.alert.message")} action={<a href={t("my:ctas.contactSupport.url")}>{t("my:ctas.contactSupport.text")}</a>} />;
    $[10] = isShowingError;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <div className="flex flex-col pt-comfy-xl gap-comfy-lg">{t2}{t3}{t4}{t5}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export const ChildBio: React.FC<ChildBioProps> = t0 => {
  const $ = _c(3);
  const {
    id
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChildBioSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== id) {
    t2 = <Suspense fallback={t1}><ChildBioContent id={id} /></Suspense>;
    $[1] = id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};